import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Flex, Text, Switch, Button, useToast, VStack, HStack,
    Badge, Divider, Textarea, Table, Thead, Tbody, Tr, Th, Td,
    TableContainer, IconButton, SimpleGrid, Tooltip, Link,
} from '@chakra-ui/react';
import { FaSyncAlt, FaExternalLinkAlt } from 'react-icons/fa';
import { getBotPerguntasMl, updateBotPerguntasMlConfig } from '../services/api';

const statsVazias = {
    hoje: { respondidas: 0, puladas: 0, erros: 0 },
    semana: { respondidas: 0, puladas: 0, erros: 0, custo_usd: 0 },
};

const STATUS_BADGE = {
    respondida: { colorScheme: 'green', label: 'respondida' },
    pulada: { colorScheme: 'yellow', label: 'pulada' },
    erro: { colorScheme: 'red', label: 'erro' },
};

const BotPerguntasMlTab = () => {
    const toast = useToast();
    const [config, setConfig] = useState({ ativo: false, system_prompt: '' });
    const [systemPromptPadrao, setSystemPromptPadrao] = useState('');
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState(statsVazias);
    const [saving, setSaving] = useState(false);
    const [salvandoPrompt, setSalvandoPrompt] = useState(false);

    const carregar = useCallback(async (incluirConfig = false) => {
        try {
            const { data } = await getBotPerguntasMl();
            if (incluirConfig) {
                setConfig({
                    ativo: !!data.config?.ativo,
                    system_prompt: data.config?.system_prompt || '',
                });
                setSystemPromptPadrao(data.system_prompt_padrao || '');
            }
            setLogs(data.logs || []);
            setStats(data.stats || statsVazias);
        } catch (e) {
            toast({ title: 'Erro ao carregar o bot de perguntas ML.', status: 'error', duration: 4000, isClosable: true });
        }
    }, [toast]);

    useEffect(() => {
        carregar(true);
        const t = setInterval(() => carregar(false), 20000);
        return () => clearInterval(t);
    }, [carregar]);

    const salvarConfig = async (overrides, mensagem) => {
        setSaving(true);
        try {
            const { data } = await updateBotPerguntasMlConfig({
                ativo: config.ativo,
                ...overrides,
            });
            setConfig((c) => ({
                ...c,
                ativo: !!data?.ativo,
                system_prompt: data?.system_prompt || c.system_prompt,
            }));
            if (mensagem) {
                toast({ title: mensagem, status: 'success', duration: 3000, isClosable: true });
            }
        } catch (e) {
            toast({ title: 'Erro ao salvar a configuração.', status: 'error', duration: 4000, isClosable: true });
        } finally {
            setSaving(false);
        }
    };

    const toggleBot = (ativo) => {
        salvarConfig({ ativo }, ativo ? 'Bot de perguntas ML ativado.' : 'Bot de perguntas ML desativado.');
    };

    const salvarPrompt = async () => {
        setSalvandoPrompt(true);
        try {
            await salvarConfig(
                { system_prompt: config.system_prompt || null },
                config.system_prompt ? 'Prompt salvo.' : 'Prompt limpo — o bot voltou a usar o padrão.'
            );
        } finally {
            setSalvandoPrompt(false);
        }
    };

    const formatarData = (iso) => {
        if (!iso) return '-';
        const d = new Date(String(iso).replace(' ', 'T'));
        if (isNaN(d.getTime())) return '-';
        return d.toLocaleString('pt-BR', {
            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
        });
    };

    return (
        <VStack spacing={5} align="stretch">

            {/* Status */}
            <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                <Flex align="center" justify="space-between" wrap="wrap" gap={4}>
                    <Box flex="1" minW={{ base: 'full', md: '260px' }}>
                        <Text fontWeight="bold" fontSize="lg">Responder perguntas dos anúncios (Mercado Livre)</Text>
                        <Text fontSize="sm" color="gray.500">
                            A cada 5 minutos a IA (Sonnet) lê as perguntas sem resposta das contas 1, 2 e 3
                            (últimas 72h) e responde as que dá pra responder com segurança, só com dados
                            do anúncio e links do próprio Mercado Livre. O que ela não sabe fica como
                            pendente pra equipe na tela de Perguntas.
                        </Text>
                    </Box>
                    <HStack spacing={3}>
                        <Badge
                            colorScheme={config.ativo ? 'green' : 'gray'}
                            fontSize="0.8em" px={2} py={1} borderRadius="md"
                        >
                            {config.ativo ? 'LIGADO' : 'DESLIGADO'}
                        </Badge>
                        <Switch
                            size="lg" colorScheme="orange"
                            isChecked={!!config.ativo}
                            isDisabled={saving}
                            onChange={(e) => toggleBot(e.target.checked)}
                        />
                    </HStack>
                </Flex>

                <Divider my={4} />

                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                    <Box borderRadius="md" border="1px solid" borderColor="gray.200" p={3}>
                        <Text fontSize="xs" color="gray.500">Respondidas hoje</Text>
                        <Text fontSize="xl" fontWeight="bold" color="green.600">{stats.hoje.respondidas}</Text>
                    </Box>
                    <Box borderRadius="md" border="1px solid" borderColor="gray.200" p={3}>
                        <Text fontSize="xs" color="gray.500">Deixadas pra equipe hoje</Text>
                        <Text fontSize="xl" fontWeight="bold" color="yellow.600">{stats.hoje.puladas}</Text>
                    </Box>
                    <Box borderRadius="md" border="1px solid" borderColor="gray.200" p={3}>
                        <Text fontSize="xs" color="gray.500">Erros hoje</Text>
                        <Text fontSize="xl" fontWeight="bold" color="red.500">{stats.hoje.erros}</Text>
                    </Box>
                    <Box borderRadius="md" border="1px solid" borderColor="gray.200" p={3}>
                        <Text fontSize="xs" color="gray.500">Custo IA (7 dias)</Text>
                        <Text fontSize="xl" fontWeight="bold">US$ {Number(stats.semana.custo_usd || 0).toFixed(2)}</Text>
                        <Text fontSize="xs" color="gray.400">
                            {stats.semana.respondidas} respondidas na semana
                        </Text>
                    </Box>
                </SimpleGrid>
            </Box>

            {/* Comportamento (system prompt) */}
            <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                <Text fontWeight="bold" fontSize="lg" mb={1}>Comportamento do bot</Text>
                <Text fontSize="sm" color="gray.600" mb={3}>
                    Prompt que o bot usa pra decidir entre responder ou deixar pra equipe. As regras de
                    segurança importantes (não afirmar o que não está no anúncio, nunca citar canal fora
                    do Mercado Livre) estão aqui — cuidado ao editar.
                </Text>
                <Textarea
                    value={config.system_prompt || ''}
                    onChange={(e) => setConfig((c) => ({ ...c, system_prompt: e.target.value }))}
                    minH={{ base: '260px', md: '380px' }}
                    fontSize="sm"
                    fontFamily="monospace"
                />
                <Flex align="center" gap={3} wrap="wrap" pt={3}>
                    <Button
                        colorScheme="orange"
                        size="sm"
                        onClick={salvarPrompt}
                        isLoading={salvandoPrompt || saving}
                    >
                        Salvar prompt
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setConfig((c) => ({ ...c, system_prompt: systemPromptPadrao }))}
                        isDisabled={!systemPromptPadrao || config.system_prompt === systemPromptPadrao}
                    >
                        Restaurar padrão
                    </Button>
                </Flex>
            </Box>

            {/* Atividade recente */}
            <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                <Flex align="center" justify="space-between" mb={3} gap={2}>
                    <Box flex="1" minW={0}>
                        <Text fontWeight="bold" fontSize="lg">Atividade recente</Text>
                        <Text fontSize="sm" color="gray.500">
                            Últimas perguntas processadas — respondida (enviada ao ML), pulada (ficou pra equipe) ou erro.
                        </Text>
                    </Box>
                    <IconButton
                        aria-label="Atualizar" icon={<FaSyncAlt />} size="sm" variant="ghost"
                        onClick={() => carregar(false)}
                    />
                </Flex>

                {logs.length === 0 ? (
                    <Text fontSize="sm" color="gray.400" textAlign="center" py={4}>
                        Nenhuma pergunta processada ainda.
                    </Text>
                ) : (
                    <TableContainer>
                        <Table size="sm" variant="simple">
                            <Thead>
                                <Tr>
                                    <Th>Quando</Th>
                                    <Th>Conta</Th>
                                    <Th>Anúncio / Pergunta</Th>
                                    <Th>Resposta do bot / Motivo</Th>
                                    <Th>Status</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {logs.map((l) => {
                                    const badge = STATUS_BADGE[l.status] || { colorScheme: 'gray', label: l.status };
                                    return (
                                        <Tr key={l.id}>
                                            <Td whiteSpace="nowrap" fontSize="xs">{formatarData(l.created_at)}</Td>
                                            <Td fontSize="xs">{l.account_id}</Td>
                                            <Td fontSize="xs" maxW={{ base: '180px', md: '260px' }} whiteSpace="normal">
                                                {l.permalink ? (
                                                    <Tooltip label="Abrir o anúncio no Mercado Livre">
                                                        <Link
                                                            href={l.permalink} isExternal
                                                            color="blue.500" display="inline-flex"
                                                            alignItems="center" gap="4px" noOfLines={1}
                                                        >
                                                            {l.ml_item_id} · {l.item_title || 'ver anúncio'}
                                                            <FaExternalLinkAlt size={10} />
                                                        </Link>
                                                    </Tooltip>
                                                ) : (l.item_title || l.ml_item_id) && (
                                                    <Text color="gray.500" noOfLines={1}>
                                                        {l.ml_item_id}{l.item_title ? ` · ${l.item_title}` : ''}
                                                    </Text>
                                                )}
                                                <Text fontWeight="medium">{l.question_text || '-'}</Text>
                                            </Td>
                                            <Td fontSize="xs" maxW={{ base: '200px', md: '340px' }} whiteSpace="normal">
                                                {l.status === 'respondida' ? (l.resposta || '-') : (l.motivo || '-')}
                                            </Td>
                                            <Td>
                                                <Tooltip label={l.status === 'pulada' ? 'A pergunta continua pendente na tela de Perguntas' : ''}>
                                                    <Badge colorScheme={badge.colorScheme}>{badge.label}</Badge>
                                                </Tooltip>
                                            </Td>
                                        </Tr>
                                    );
                                })}
                            </Tbody>
                        </Table>
                    </TableContainer>
                )}
            </Box>

        </VStack>
    );
};

export default BotPerguntasMlTab;
